import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import posthog from "../../config/posthog";
import { persistUtmParams } from "../../utils/utmStorage";

/**
 * Captures UTM / ad-click params from the URL and fires the page-view
 * trackers that the OfferCheckout analytics hook does NOT cover:
 *   - PostHog page-load event
 *   - Microsoft Clarity
 *
 * (Meta Pixels x3, Plausible, Pagesense, GTM, GA4 + the iOS no-zoom viewport
 *  are injected by useOfferAnalyticsScripts, which the page also calls.)
 *
 * Returns the captured utmParams so the form can forward them to
 * create-intent (and the redirect URL) — attribution reaches the lead record.
 */
const UTM_PARAM_NAMES = [
  // Standard UTM
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "utm_id",
  // Google Ads
  "campaign_id",
  "adgroup_id",
  "ad_id",
  "creative",
  "keyword",
  "device",
  "matchtype",
  "network",
  "placement",
  "gclid",
  "gad_source",
  "gad_campaignid",
  // Meta / other click IDs
  "fbclid",
  "msclkid",
  "ttclid",
  "twclid",
  "li_fat_id",
  "sub_source",
  "source_medium",
  "landing_page_url",
  "referrer_url",
  "first_touch_source",
  "latest_touch_source",
  "ad_created_at",
];

export default function useWebinarTracking(slug) {
  const location = useLocation();
  const [utmParams, setUtmParams] = useState({});

  // Capture UTM / ad-click params from the URL.
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const captured = {};
    UTM_PARAM_NAMES.forEach((param) => {
      const value = urlParams.get(param);
      if (value) captured[param] = value;
    });
    setUtmParams(captured);

    persistUtmParams(captured);
  }, [location.search]);

  // PostHog page-load event.
  useEffect(() => {
    try {
      posthog.capture("webinar_page_loaded", {
        slug,
        timestamp: new Date().toISOString(),
        utm_params: utmParams,
      });
    } catch (err) {
      // analytics must never break the page
    }
    // fire once per slug, after first UTM capture
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Microsoft Clarity (present on RegionalPlansPage, not in the offer hook).
  useEffect(() => {
    const clarityScriptId = "clarity-script";
    if (document.getElementById(clarityScriptId)) return;
    const clarityScript = document.createElement("script");
    clarityScript.id = clarityScriptId;
    clarityScript.type = "text/javascript";
    clarityScript.innerHTML = `
      (function(c,l,a,r,i,t,y){
        c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
        t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
        y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
      })(window, document, "clarity", "script", "v6d5f4kx6j");
    `;
    document.head.appendChild(clarityScript);
    return () => {
      const el = document.getElementById(clarityScriptId);
      if (el) el.remove();
    };
  }, []);

  return utmParams;
}
